import React, { useEffect, useMemo } from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import { useTranslation } from "react-i18next";
import BaseNotFound from "@reusables/BaseNotFound";
import { z } from "zod";
import { FieldPath, useFieldArray, useForm } from "react-hook-form";
import { SerialGroup } from "./SerialGroup";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { zodResolver } from "@hookform/resolvers/zod";
import { stopPropagate } from "@helpers/utils";
import i18n from "@/i18n";
import { useSerialNumberOptions } from "./hooks";

/**
 * This modal handles the validation of serial numbers.
 * Users can attach these numbers (click the "attach" button) if they meet the following criteria:
 * 1. All input fields are filled.
 * 2. For serial numbers, the number of inputs should not exceed the selected quantity. This applies to every product line.
 *
 * Once serial numbers are attached, users have the option to modify them or use them during the creation of a receipt record.
 * If used in the receipt creation, there might be instances where the number of serials is less than the selected quantity.
 * In such cases:
 * 1. Users can edit and add more serial numbers.
 * 2. An error message will be displayed, prompting the user to match the number of serials with the selected quantity.
 */



const formScheme = z.object({
    receipts: z.array(
        z.object({
            sale_order_line_id: z.number(),

            product: z.object({
                id: z.number(),
                name: z.string(),
                code: z.string()
            }),

            location: z.object({
                store: z.object({
                    id: z.number(),
                    name: z.string()
                }),
                section: z.object({
                    id: z.number().optional(),
                    name: z.string().optional()
                }).optional()
            }).optional(),

            received_quantity: z.coerce.number(),

            // Serial_number cannot be empty, however because of RHF bug
            // we cannot correctly validate the serials, if they are added to the end of the array
            // therefore we add them with a `prepend` method.
            serial_numbers: z.array(
                z.object({
                    serial_number: z.string().nonempty()
                })
            )
        }).refine(
            scheme => scheme.serial_numbers.length <= scheme.received_quantity,
            {
                message: i18n.t("receive.modals.serialNumbers.validation.serialsCannotBeGreaterThanReceivedQuantity"),
                path: ["serial_numbers"]
            }
        )
    )
})
    .superRefine((scheme, ctx) => {
        // Within-session duplicate validation across all receipts
        const serialNumberPaths: Record<string, (string | number)[][]> = {};

        scheme.receipts.forEach((receipt, receiptIndex) => {
            receipt.serial_numbers.forEach((serial, serialIndex) => {
                const serialNumber = serial.serial_number;
                const path = [receiptIndex, "serial_numbers", serialIndex];
                if (!serialNumberPaths[serialNumber]) serialNumberPaths[serialNumber] = [];
                serialNumberPaths[serialNumber].push(path);
            });
        });

        Object.entries(serialNumberPaths).forEach(([serialNumber, paths]) => {
            if (serialNumber && paths.length > 1) {
                paths.forEach(path => {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        params: {
                            i18n: {
                                key: "receive.modals.serialNumbers.validation.serialNumberDuplicate"
                            }
                        },
                        path: ["receipts", ...path, "serial_number"]
                    });
                });
            }
        });
    });

export type SerialNumbersScheme = z.infer<typeof formScheme>
export type SerialNumberAutocompleteOption = { serial_number: string };

interface IncomingReceipt {
    order: {
        data: {
            id: number;
            product: {
                id: number;
                name: string;
                code: string;
            }
        }
    };
    location?: {
        store: {
            id: number;
            name: string;
        }
        section?: {
            id?: number;
            name: string;
        }
    };
    received_quantity?: number | null;
    serial_numbers?: {
        serial_number: string;
    }[];
}

interface SerialNumbersModalProps {
    isOpen: boolean;
    onClose: () => void;

    receipts: IncomingReceipt[];
    onSave?: (data: SerialNumbersScheme) => void;
    preview?: boolean;
}

export default function SerialNumbersModal(props: SerialNumbersModalProps) {
    const { t } = useTranslation("", { keyPrefix: "receive.modals.serialNumbers" });

    const { control, handleSubmit, setValue, reset, setError } = useForm<SerialNumbersScheme>({
        resolver: zodResolver(formScheme)
    });

    const { fields: receipt } = useFieldArray({
        control,
        name: "receipts"
    });

    // Extract product IDs for serial number options fetching
    const productIds = useMemo(() =>
            receipt.reduce((acc: number[], line: any) => {
                if (line.product?.id && !acc.includes(line.product.id)) {
                    acc.push(line.product.id);
                }
                return acc;
            }, []),
        [receipt]);

    // Load globally existing serial numbers for products (used only for validation)
    const { serialNumbersOptions, isLoading } = useSerialNumberOptions(productIds);

    const onSubmit = handleSubmit((data) => {
        // Validate against globally existing serial numbers (conflict)
        let hasGlobalConflicts = false;

        data.receipts.forEach((rec, receiptIndex) => {
            const options: SerialNumberAutocompleteOption[] = serialNumbersOptions[rec.product.id] ?? [];
            const optionSet = new Set(options.map((opt: SerialNumberAutocompleteOption) => opt.serial_number));
            rec.serial_numbers.forEach((sn, serialIndex) => {
                if (sn.serial_number && optionSet.has(sn.serial_number)) {
                    hasGlobalConflicts = true;
                    const path = `receipts.${receiptIndex}.serial_numbers.${serialIndex}.serial_number` as FieldPath<SerialNumbersScheme>;
                    setError(path, {
                        type: "validate",
                        message: t("validation.serialNumberAlreadyExists", { defaultValue: "Serial number already exists" })
                    });
                }
            });
        });

        if (hasGlobalConflicts) {
            return;
        }

        props.onSave?.(data);
    });

    useEffect(() => {
        if (props.isOpen) {
            const formReadyLines = props.receipts
                .filter(x => !!x.location)
                .map(receipt => {
                    const serial_numbers = receipt.serial_numbers?.length ? receipt.serial_numbers : [{ serial_number: "" }];

                    const location = receipt.location as NonNullable<IncomingReceipt["location"]>;

                    return {
                        sale_order_line_id: receipt.order.data.id,
                        product: {
                            id: receipt.order.data.product.id,
                            name: receipt.order.data.product.name,
                            code: receipt.order.data.product.code
                        },
                        location: {
                            store: {
                                id: location.store.id,
                                name: location.store.name
                            },
                            section: {
                                id: location.section?.id,
                                name: location.section?.name
                            }
                        },
                        received_quantity: receipt.received_quantity ?? 0,
                        serial_numbers
                    };
                });

            setValue(
                "receipts",
                formReadyLines
            );
        } else {
            reset();
        }
    }, [props.receipts, props.isOpen, setValue, reset]);

    return <BaseModal
        isOpen={props.isOpen}
        onClose={props.onClose}
        width={900}
        padding="56px"
        position={"center"}
        useCloseIcon={props.preview}
        isLoading={isLoading}
    >
        <form onSubmit={stopPropagate(onSubmit)} className="space-y-[32px]">
            {
                receipt.length ?
                    receipt.map((line, lineIndex) => (
                        <SerialGroup
                            key={line.id}
                            index={lineIndex}
                            control={control}
                            preview={props.preview}
                        />
                    ))
                    :
                    <BaseNotFound text={t("noProductsPicked")} height={250} />
            }
            {
                !props.preview &&
                <BaseInputsGrid cols={2}>
                    {/* "Cancel" button */}
                    <BaseButton text={t("buttons.cancel")}
                                size="md"
                                buttonWidth={"100%"}
                                primaryOutlined
                                type={"button"}
                                onClick={props.onClose}
                    />

                    {/* "Attach" button */}
                    <BaseButton text={t("buttons.attach")}
                                size="md"
                                buttonWidth={"100%"}
                    />
                </BaseInputsGrid>
            }
        </form>
    </BaseModal>;
}